import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { useSearchParams } from "react-router-dom"

import { cn } from "@/lib/utils"

interface TabsProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  /**
   * If provided, sync the active tab with this URL search param.
   * Reading and writing happens transparently — pages keep working on reload,
   * back/forward navigation, and link sharing.
   */
  urlParam?: string
  /**
   * If provided, an URL value outside this whitelist is ignored and the tab
   * falls back to `defaultValue`. Guards deep-links to tabs the current user is
   * not allowed to open (e.g. `?tab=organization` for a role without admin).
   */
  allowedValues?: string[]
}

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(({ urlParam, allowedValues, defaultValue, value, onValueChange, ...props }, ref) => {
  const [searchParams, setSearchParams] = useSearchParams()

  if (!urlParam) {
    return (
      <TabsPrimitive.Root
        ref={ref}
        defaultValue={defaultValue}
        value={value}
        onValueChange={onValueChange}
        {...props}
      />
    )
  }

  const fallback =
    (typeof value === "string" ? value : undefined) ??
    (typeof defaultValue === "string" ? defaultValue : undefined)
  const rawValue = searchParams.get(urlParam) || fallback
  const urlValue =
    allowedValues && rawValue !== undefined && !allowedValues.includes(rawValue)
      ? fallback
      : rawValue

  function handleChange(newValue: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (newValue && newValue !== fallback) {
          next.set(urlParam!, newValue)
        } else {
          next.delete(urlParam!)
        }
        return next
      },
      { replace: true }
    )
    onValueChange?.(newValue)
  }

  return (
    <TabsPrimitive.Root
      ref={ref}
      value={urlValue}
      onValueChange={handleChange}
      {...props}
    />
  )
})
Tabs.displayName = "Tabs"

/**
 * Scrolls horizontally when the triggers overflow, and only horizontally.
 *
 * An `overflow-x: auto` box computes `overflow-y` to `auto` as well (CSS rule),
 * so with a fixed `h-10` the horizontal scrollbar ate the height and a second,
 * vertical scrollbar appeared next to the triggers. Hence `overflow-y-hidden`
 * plus `min-h-10`: the box grows by the scrollbar thickness instead of
 * clipping. `justify-start` keeps the first trigger reachable: a centered flex
 * line that overflows pushes its start into the negative, unscrollable area.
 */
const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "scrollbar-thin inline-flex min-h-10 max-w-full items-center justify-start overflow-x-auto overflow-y-hidden rounded-xl bg-muted/50 p-1 text-muted-foreground",
      className
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
